"use client";

import { config } from "@repo/config";
import { logger } from "@repo/logs";
import { getAwsCredentialsAction } from "@saas/admin/lib/aws/actions";
import type { AwsCredentials } from "@saas/shared/types/viewing";
import { useCallback, useEffect, useRef, useState } from "react";

export interface AwsCredentialsState {
  credentials: AwsCredentials | null;
  identityId: string | null;
  developerUserIdentifier: string | null;
  loading: boolean;
  error: string | null;
  lastExchanged: Date | null;
}

export interface UseAwsCredentialsReturn extends AwsCredentialsState {
  exchangeCredentials: () => Promise<AwsCredentialsState | null>;
  clearCredentials: () => void;
  isExpired: () => boolean;
}

// Global credential store - persists across page loads and navigation
interface CredentialStore {
  credentials: AwsCredentials | null;
  identityId: string | null;
  developerUserIdentifier: string | null;
  lastExchanged: Date | null;
  organizationId: string | null;
  userId: string | null;
}

// Global state management
let globalStore: CredentialStore = {
  credentials: null,
  identityId: null,
  developerUserIdentifier: null,
  lastExchanged: null,
  organizationId: null,
  userId: null,
};

// Single-flight guard for concurrent exchanges
let inflightExchange: Promise<CredentialStore | null> | null = null;

// Subscribers for reactive updates
const subscribers = new Set<() => void>();

// Storage keys
const STORAGE_KEY = "aws_credentials_cache";
const STORAGE_ORG_KEY = "aws_credentials_org";

/**
 * Load credentials from localStorage on module load
 */
function loadFromStorage(): void {
  try {
    if (typeof window === "undefined") {
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    const storedOrg = localStorage.getItem(STORAGE_ORG_KEY);

    if (stored) {
      const parsed = JSON.parse(stored);
      // Check if credentials are not expired
      if (parsed.credentials?.expiration) {
        const expirationTime = new Date(parsed.credentials.expiration).getTime();
        const currentTime = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

        if (expirationTime - currentTime > bufferTime) {
          // Normalize credentials to ensure expiration is a Date object
          const normalizedCredentials = {
            ...parsed.credentials,
            expiration: new Date(parsed.credentials.expiration),
          };

          globalStore = {
            ...parsed,
            credentials: normalizedCredentials,
            lastExchanged: parsed.lastExchanged ? new Date(parsed.lastExchanged) : null,
            organizationId: storedOrg,
          };
          console.log("🔑 Loaded valid AWS credentials from cache", {
            identityId: globalStore.identityId,
            organizationId: globalStore.organizationId,
            expiresAt: normalizedCredentials.expiration,
          });
        } else {
          console.log("🔑 Cached AWS credentials expired, will refresh");
          clearStorage();
        }
      }
    }
  } catch (error) {
    console.error("Failed to load AWS credentials from cache:", error);
    clearStorage();
  }
}

/**
 * Save credentials to localStorage
 */
function saveToStorage(store: CredentialStore): void {
  try {
    if (typeof window === "undefined") {
      return;
    }

    if (store.credentials) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          credentials: store.credentials,
          identityId: store.identityId,
          developerUserIdentifier: store.developerUserIdentifier,
          lastExchanged: store.lastExchanged,
          userId: store.userId,
        })
      );

      if (store.organizationId) {
        localStorage.setItem(STORAGE_ORG_KEY, store.organizationId);
      }

      console.log("🔑 Saved AWS credentials to cache", {
        identityId: store.identityId,
        organizationId: store.organizationId,
      });
    }
  } catch (error) {
    console.error("Failed to save AWS credentials to cache:", error);
  }
}

/**
 * Clear credentials from localStorage
 */
function clearStorage(): void {
  try {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_ORG_KEY);
  } catch (error) {
    console.error("Failed to clear AWS credentials cache:", error);
  }
}

/**
 * Update global store and notify all subscribers
 */
function updateGlobalStore(updates: Partial<CredentialStore>): void {
  globalStore = { ...globalStore, ...updates };
  saveToStorage(globalStore);

  // Notify all subscribers
  subscribers.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      console.error("Error in AWS credentials subscriber:", error);
    }
  });
}

/**
 * Subscribe to credential changes
 */
function subscribe(callback: () => void): () => void {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * Check if current credentials are expired
 */
function isCredentialsExpired(): boolean {
  if (!globalStore.credentials?.expiration) {
    return true;
  }

  const expirationTime = new Date(globalStore.credentials.expiration).getTime();
  const currentTime = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

  return expirationTime - currentTime <= bufferTime;
}

/**
 * Exchange JWT token for AWS credentials (global function)
 */
async function exchangeCredentialsGlobal(
  userId: string | null,
  organizationId: string | null
): Promise<CredentialStore | null> {
  // Proceed with exchange if backend is configured; don't hard-block on frontend flag

  // Check if we already have valid credentials for this user/org
  if (
    globalStore.credentials &&
    globalStore.userId === userId &&
    globalStore.organizationId === organizationId &&
    !isCredentialsExpired()
  ) {
    console.log("🔑 Using cached AWS credentials", {
      identityId: globalStore.identityId,
      organizationId: globalStore.organizationId,
    });
    return globalStore;
  }

  // If an exchange is already in progress, await it to avoid thundering herd
  if (inflightExchange) {
    return inflightExchange;
  }

  console.log("🔑 Exchanging AWS credentials", {
    userId,
    organizationId,
    reason: globalStore.credentials ? "expired/different-context" : "no-credentials",
  });

  inflightExchange = (async () => {
    try {
      const result = await getAwsCredentialsAction({
        identityPoolId: config.aws.identityPoolId,
        region: config.aws.region,
        developerProviderName: config.aws.developerProviderName,
        organizationId: organizationId || null,
      });

      // Normalize credentials to ensure expiration is a Date object
      const normalizedCredentials = result.credentials?.expiration
        ? {
            ...result.credentials,
            expiration: new Date(result.credentials.expiration),
          }
        : result.credentials;

      const newStore: CredentialStore = {
        credentials: normalizedCredentials as any,
        identityId: result.identityId,
        developerUserIdentifier: result.developerUserIdentifier,
        lastExchanged: new Date(),
        organizationId: organizationId || null,
        userId: userId || null,
      };

      updateGlobalStore(newStore);

      logger.info("AWS credentials exchanged successfully", {
        identityId: result.identityId,
        userId,
        organizationId,
      });

      return newStore;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to exchange AWS credentials";

      logger.error("Failed to exchange AWS credentials", {
        error: errorMessage,
        userId,
        organizationId,
      });

      console.error("AWS credentials exchange failed:", errorMessage);
      throw error;
    } finally {
      inflightExchange = null;
    }
  })();

  return inflightExchange;
}

/**
 * Clear all credentials (global function)
 */
function clearCredentialsGlobal(): void {
  updateGlobalStore({
    credentials: null,
    identityId: null,
    developerUserIdentifier: null,
    lastExchanged: null,
    organizationId: null,
    userId: null,
  });
  clearStorage();
  console.log("🔑 Cleared AWS credentials");
}

// Load credentials from storage on module initialization
loadFromStorage();

/**
 * Hook to automatically exchange JWT tokens for AWS credentials
 * Now uses a global persistent store that survives navigation and page refreshes
 */
export function useAwsCredentials(
  userId?: string | null,
  organizationId?: string | null,
  autoExchange = config.aws.autoExchangeCredentialsOnLogin
): UseAwsCredentialsReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, forceUpdate] = useState({});
  
  // Track previous userId to detect actual logout vs temporary null
  const prevUserIdRef = useRef<string | null | undefined>(userId);

  // Force re-render when global store changes
  useEffect(() => {
    return subscribe(() => {
      forceUpdate({});
    });
  }, []);

  /**
   * Check if credentials are expired or close to expiring (within 5 minutes)
   */
  const isExpired = useCallback(() => {
    return isCredentialsExpired();
  }, []);

  /**
   * Exchange JWT token for AWS credentials
   */
  const exchangeCredentials = useCallback(async (): Promise<AwsCredentialsState | null> => {
    setLoading(true);
    setError(null);

    try {
      const result = await exchangeCredentialsGlobal(userId || null, organizationId || null);

      if (result) {
        return {
          credentials: result.credentials,
          identityId: result.identityId,
          developerUserIdentifier: result.developerUserIdentifier,
          loading: false,
          error: null,
          lastExchanged: result.lastExchanged,
        };
      }

      return null;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to exchange AWS credentials";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, organizationId]);

  /**
   * Clear stored credentials
   */
  const clearCredentials = useCallback(() => {
    clearCredentialsGlobal();
    setError(null);
  }, []);

  /**
   * Handle organization context changes without causing visible flicker.
   * - Normalize undefined to null for stable comparisons
   * - If no credentials yet, just sync org locally (no clear)
   * - If credentials exist, hot-refresh credentials instead of clearing first
   */
  useEffect(() => {
    const nextOrg = organizationId ?? null;
    const prevOrg = globalStore.organizationId;

    if (prevOrg === nextOrg) {
      return;
    }

    // Always keep the store in sync to avoid repeated diffs
    updateGlobalStore({ organizationId: nextOrg });

    // If there's no user, skip credential refresh
    if (!userId) {
      return;
    }

    // If we don't have credentials yet, let the auto-exchange handle it
    if (!globalStore.credentials) {
      return;
    }

    // Only refresh if credentials are for a different org and we have a user
    // This prevents unnecessary refreshes during navigation
    if (prevOrg !== null && nextOrg !== prevOrg) {
      logger.info("Organization changed, refreshing AWS credentials", {
        userId,
        previousOrganizationId: prevOrg,
        newOrganizationId: nextOrg,
      });

      // Smooth refresh without dropping existing creds to avoid reconnect flicker
      exchangeCredentials();
    }
  }, [organizationId, userId, exchangeCredentials]);

  /**
   * Auto-exchange credentials when user logs in or organization changes
   */
  useEffect(() => {
    if (autoExchange && userId && !globalStore.credentials && !loading && !error) {
      console.log("🔑 Auto-exchanging AWS credentials for new user/org");
      exchangeCredentials();
    }
  }, [autoExchange, userId, organizationId, loading, error, exchangeCredentials]);

  /**
   * Auto-refresh credentials when they're close to expiring.
   * Respects autoExchange to avoid calling cookie-gated server actions
   * in contexts without a cookie session (e.g. embed).
   */
  useEffect(() => {
    if (!autoExchange) {
      return;
    }

    if (!globalStore.credentials || !userId) {
      return;
    }

    if (isExpired() && !loading && !error) {
      logger.info("AWS credentials expired, refreshing...", {
        userId,
        organizationId,
      });
      exchangeCredentials();
    }
  }, [autoExchange, userId, organizationId, isExpired, loading, error, exchangeCredentials]);

  /**
   * DISABLED: Automatic credential clearing on userId change
   * 
   * This was causing issues during navigation and component remounting where userId
   * would temporarily become null, triggering unwanted credential clearing.
   * 
   * Credentials are now only cleared:
   * 1. Explicitly via clearCredentials() (e.g., logout button)
   * 2. When they expire (handled by auto-refresh logic)
   * 3. When organization changes (handled by org switching logic with refresh)
   * 
   * If you need to clear credentials on logout, call clearCredentials() explicitly
   * from your logout handler.
   */
  useEffect(() => {
    // Update ref to track userId changes for potential future use
    prevUserIdRef.current = userId;
    
    // No automatic clearing - let the application explicitly manage credential lifecycle
  }, [userId]);

  return {
    credentials: globalStore.credentials,
    identityId: globalStore.identityId,
    developerUserIdentifier: globalStore.developerUserIdentifier,
    loading,
    error,
    lastExchanged: globalStore.lastExchanged,
    exchangeCredentials,
    clearCredentials,
    isExpired,
  };
}

/**
 * Seed the global credential store from an external source (e.g., embed provider).
 * This updates both the in-memory store and localStorage, and notifies all subscribers
 * so that useAwsCredentials hooks pick up the new credentials immediately.
 */
export function seedAwsCredentials(data: {
  credentials: AwsCredentials;
  identityId: string;
  developerUserIdentifier: string;
  userId: string;
  organizationId: string;
}): void {
  updateGlobalStore({
    credentials: {
      ...data.credentials,
      expiration: data.credentials.expiration
        ? new Date(data.credentials.expiration)
        : undefined,
    } as any,
    identityId: data.identityId,
    developerUserIdentifier: data.developerUserIdentifier,
    lastExchanged: new Date(),
    userId: data.userId,
    organizationId: data.organizationId,
  });
}

/**
 * Direct accessors for current AWS credentials and identity.
 * Useful for libraries (e.g., Amplify credentialsProvider) that cannot use React hooks.
 */
export function getCurrentAwsCredentials(): AwsCredentials | null {
  return globalStore.credentials;
}

export function getCurrentIdentityId(): string | null {
  return globalStore.identityId;
}
